/**
 * Derives the delivery case from the operator's two booleans.
 *
 * APP-2427 stores a derived case name in the audit log alongside the raw
 * booleans, because "two booleans read poorly in a trail". They read no
 * better in a form, so the same four cases are named here and echoed to the
 * operator before they submit. The names match the backend's quadrant table.
 *
 * Pure, and returns a *key* plus interpolation values, never display text.
 */
import type { SalesforceOpportunityIngestRequest } from '@/generated/http-clients/v3ApiSchemas'

export type ResolutionCaseKey =
  /** `createNewOrg: false, createNewAccount: false` — licences into the named org. */
  | 'existingOrg'
  /** `true, false` — new org under the existing account. */
  | 'newOrgExistingAccount'
  /** `true, true` — new account and new org. */
  | 'newOrgNewAccount'
  /** `false, true` — new account, added to the named org, licences there. */
  | 'existingOrgNewAccount'

export type ResolutionDecision = {
  createNewOrg: boolean
  createNewAccount: boolean
  /** Trimmed. Only meaningful when `createNewOrg` is false. */
  orgId: string
}

export type ResolutionDescription = {
  caseKey: ResolutionCaseKey
  /** Interpolation values for the case's i18n string. */
  values: {
    email: string
    company: string
    orgId: string
    licenceTotal: number
  }
}

/**
 * A lookup rather than a ternary chain: Biome's `noNestedTernary` is on, and
 * four cases keyed by two flags is exactly what a map is for.
 */
const CASE_KEYS: Record<string, ResolutionCaseKey> = {
  'false|false': 'existingOrg',
  'true|false': 'newOrgExistingAccount',
  'true|true': 'newOrgNewAccount',
  'false|true': 'existingOrgNewAccount',
}

export function resolutionCaseKey({
  createNewOrg,
  createNewAccount,
}: Pick<
  ResolutionDecision,
  'createNewOrg' | 'createNewAccount'
>): ResolutionCaseKey {
  return CASE_KEYS[`${createNewOrg}|${createNewAccount}`]
}

/**
 * Pairs the case key with the values its sentence interpolates.
 *
 * Takes the parsed payload, so callers must only describe a resolution once
 * the paste has parsed. A sentence naming the owner and the licence count
 * cannot be written before those are known.
 */
export function describeResolution(
  decision: ResolutionDecision,
  payload: SalesforceOpportunityIngestRequest,
  licenceTotal: number,
): ResolutionDescription {
  return {
    caseKey: resolutionCaseKey(decision),
    values: {
      email: payload.orgOwner.email,
      company: payload.company,
      orgId: decision.orgId,
      licenceTotal,
    },
  }
}
